import { useState } from "react";

// Auto-formats keyboard input as MM/DD/YYYY and emits ISO 'YYYY-MM-DD' via onChange.
// Accepts incoming `value` as ISO 'YYYY-MM-DD' (or empty).
export default function DateInput({
    value,
    onChange,
    required = false,
    "data-testid": testid,
    placeholder = "MM/DD/YYYY",
    autoFocus = false,
    className = "sp-input",
}) {
    const [text, setText] = useState(() => isoToMasked(value));
    const [prevValue, setPrevValue] = useState(value);

    // React-recommended pattern for syncing prop->state without useEffect.
    // Tracks the last `value` we saw and adjusts `text` if the parent resets externally.
    if (value !== prevValue) {
        setPrevValue(value);
        if (value !== maskedToIso(text)) {
            setText(isoToMasked(value));
        }
    }

    const handleChange = (e) => {
        const masked = formatMask(e.target.value);
        setText(masked);
        const iso = maskedToIso(masked);
        // Emit a synthetic event-like object so existing handlers (e) => setForm... still work
        onChange({ target: { value: iso || "" } });
    };

    return (
        <input
            type="text"
            inputMode="numeric"
            autoComplete="bday"
            value={text}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            maxLength={10}
            autoFocus={autoFocus}
            className={className}
            data-testid={testid}
        />
    );
}

function formatMask(raw) {
    const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    if (digits.length <= 2) return mm;
    if (digits.length <= 4) return `${mm}/${dd}`;
    return `${mm}/${dd}/${yyyy}`;
}

function maskedToIso(masked) {
    const m = (masked || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return "";
    const [_, mm, dd, yyyy] = m;
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);
    const year = parseInt(yyyy, 10);
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return "";
    // Validate real calendar date
    const d = new Date(Date.UTC(year, month - 1, day));
    if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return "";
    return `${yyyy}-${mm}-${dd}`;
}

function isoToMasked(iso) {
    const m = (iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return "";
    return `${m[2]}/${m[3]}/${m[1]}`;
}

// Returns age in full years for ISO 'YYYY-MM-DD'. Empty string -> NaN.
export function ageInYears(isoDate, now = new Date()) {
    const m = (isoDate || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return NaN;
    const [_, y, mo, d] = m.map(Number);
    let age = now.getFullYear() - y;
    const beforeBirthday = now.getMonth() + 1 < mo || (now.getMonth() + 1 === mo && now.getDate() < d);
    if (beforeBirthday) age -= 1;
    return age;
}

export const MIN_AGE = 16;
